"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu } from "lucide-react";

import { TrackedLink } from "@/components/analytics/tracked-link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { href: "/how-it-works", label: "How it works" },
  { href: "/simulate", label: "Simulator" },
  { href: "/tracker", label: "Tracker" },
  { href: "/pricing", label: "Pricing" },
] as const;

const MOBILE_NAV_LINKS = [
  ...NAV_LINKS,
  { href: "/disclaimer", label: "Disclaimer" },
  { href: "/privacy", label: "Privacy" },
  { href: "/terms", label: "Terms" },
] as const;

export function SiteHeader() {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Persistent path to the simulator from every scroll position — the only
  // other entries are the home hero and the page-bottom closing CTA. Hidden on
  // /simulate itself, where it would point at the current page.
  const showSimulatorCta = pathname !== "/simulate";

  return (
    <header className="border-nk-border bg-nk-background/85 supports-[backdrop-filter]:bg-nk-background/70 sticky top-0 z-40 border-b backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-6">
        <Link
          href="/"
          className="font-display text-nk-foreground hover:text-nk-primary-hover text-base font-medium tracking-tight whitespace-nowrap transition-colors sm:text-lg"
          aria-label="North Kove — home"
        >
          North Kove
        </Link>

        {/* Desktop nav + persistent CTA — md and above */}
        <div className="hidden items-center gap-3 md:flex">
          <nav aria-label="Primary">
            <ul className="flex items-center gap-1">
              {NAV_LINKS.map(({ href, label }) => {
                const active = pathname === href || pathname?.startsWith(`${href}/`);
                return (
                  <li key={href}>
                    <Link
                      href={href}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "rounded-md px-3 py-1.5 text-sm whitespace-nowrap transition-colors",
                        active
                          ? "text-nk-foreground bg-nk-surface"
                          : "text-nk-foreground-muted hover:text-nk-foreground hover:bg-nk-surface/60",
                      )}
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
          {showSimulatorCta && (
            <Button asChild size="sm" variant="dark">
              <TrackedLink href="/simulate" event="simulate_cta_clicked" source="site_header">
                Run a Scenario
              </TrackedLink>
            </Button>
          )}
        </div>

        {/* Mobile hamburger — below md */}
        <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
              <Menu className="size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72">
            <SheetHeader>
              <SheetTitle className="font-display text-lg font-medium">Menu</SheetTitle>
            </SheetHeader>
            <nav aria-label="Mobile primary" className="mt-6 px-4">
              <ul className="flex flex-col gap-1">
                {MOBILE_NAV_LINKS.map(({ href, label }) => {
                  const active = pathname === href || pathname?.startsWith(`${href}/`);
                  return (
                    <li key={href}>
                      <Link
                        href={href}
                        onClick={() => setIsMenuOpen(false)}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "block rounded-md px-3 py-2.5 text-base transition-colors",
                          active
                            ? "text-nk-foreground bg-nk-surface font-medium"
                            : "text-nk-foreground-muted hover:text-nk-foreground hover:bg-nk-surface/60",
                        )}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
            {showSimulatorCta && (
              <div className="mt-6 px-4">
                <Button asChild variant="dark" className="w-full">
                  <TrackedLink
                    href="/simulate"
                    event="simulate_cta_clicked"
                    source="site_header"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Run a Scenario
                  </TrackedLink>
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
